import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    Button,
    FormControlLabel,
    Switch,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    Chip,
    InputAdornment,
    Checkbox,
    FormGroup,
} from '@mui/material';
import {
    Save as SaveIcon,
    ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import promotionService from '../../../services/promotionService';
import customerService from '../../../services/customerService';
import productService from '../../../services/productService';
import storeService from '../../../services/storeService';
import categoryService from '../../../services/categoryService';
import PromotionVariantPicker, { isBelowMinimum } from './PromotionVariantPicker';

const PromotionForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);
    const { t } = useTranslation();
    const { user } = useSelector((state) => state.auth);

    const isSuperAdminWithoutStore = user?.globalRole === 'SUPER_ADMIN' && !user?.store;

    const [formData, setFormData] = useState({
        storeId: '',
        customerId: '',
        name: '',
        code: '',
        type: 'PERCENTAGE',
        value: '',
        minimumAmount: '',
        usageLimit: '',
        startDate: '',
        endDate: '',
        isActive: true,
        productIds: [],
        targetType: 'PRODUCT',
        targetCategoryId: '',
        targetVariantId: '',
        buyQuantity: '',
        getQuantity: '',
        additionalStoreIds: [],
        additionalTargetVariantIds: [],
    });

    const [stores, setStores] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    // VARIANT targeting: variants from any number of products. The first
    // one is sent as targetVariantId, the rest as additionalTargetVariantIds.
    const [selectedVariants, setSelectedVariants] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        // Fetched unconditionally (not just for the SUPER_ADMIN create-time
        // store picker above) — the "Magasins" multi-select below needs the
        // full store list for every user/mode.
        fetchStores();
        fetchCustomers();
        fetchProducts();
        fetchCategories();
        if (isEdit) {
            fetchPromotion();
        }
    }, [id]);

    const fetchStores = async () => {
        try {
            const data = await storeService.getAllStores();
            setStores(data || []);
        } catch (err) {
            toast.error(t('stores.fetchError'));
        }
    };

    const fetchCustomers = async () => {
        try {
            const data = await customerService.getCustomers();
            setCustomers(data || []);
        } catch (err) {
            console.error('Failed to fetch customers:', err);
        }
    };

    const fetchProducts = async () => {
        try {
            const data = await productService.getAllProducts();
            setProducts(data || []);
        } catch (err) {
            console.error('Failed to fetch products:', err);
        }
    };

    const fetchCategories = async () => {
        try {
            const data = await categoryService.getAllCategories();
            setCategories(data || []);
        } catch (err) {
            console.error('Failed to fetch categories:', err);
        }
    };

    const fetchPromotion = async () => {
        try {
            const data = await promotionService.getPromotionById(id);
            setFormData({
                storeId: data.storeId || '',
                customerId: data.customerId || '',
                name: data.name || '',
                code: data.code || '',
                type: data.type || 'PERCENTAGE',
                value: data.value != null ? String(data.value) : '',
                minimumAmount: data.minimumAmount != null ? String(data.minimumAmount) : '',
                usageLimit: data.usageLimit != null ? String(data.usageLimit) : '',
                startDate: data.startDate ? data.startDate.substring(0, 10) : '',
                endDate: data.endDate ? data.endDate.substring(0, 10) : '',
                isActive: data.isActive !== undefined ? data.isActive : true,
                productIds: data.productIds || [],
                targetType: data.targetType || 'PRODUCT',
                targetCategoryId: data.targetCategoryId || '',
                targetVariantId: data.targetVariantId || '',
                buyQuantity: data.buyQuantity != null ? String(data.buyQuantity) : '',
                getQuantity: data.getQuantity != null ? String(data.getQuantity) : '',
                additionalStoreIds: data.additionalStoreIds || [],
                additionalTargetVariantIds: data.additionalTargetVariantIds || [],
            });
            // Pre-select products from the fetched list
            if (data.productIds && data.productIds.length > 0) {
                const allProducts = await productService.getAllProducts();
                setProducts(allProducts || []);
                setSelectedProducts(
                    (allProducts || []).filter((p) => data.productIds.includes(p.id))
                );
            }
            // Load every targeted variant (primary + additional) with its
            // product name and price for the selected-variants table.
            if (data.targetType === 'VARIANT' && data.targetVariantId) {
                const ids = [data.targetVariantId, ...(data.additionalTargetVariantIds || [])];
                const variants = await Promise.all(ids.map((variantId) =>
                    productService.getProductVariantById(variantId).catch(() => null)));
                setSelectedVariants(variants.filter(Boolean).map((v) => ({
                    id: v.id,
                    productId: v.productId,
                    productName: v.productName,
                    name: v.name,
                    sku: v.sku,
                    price: v.price,
                })));
            }
        } catch (error) {
            toast.error(t('promotions.fetchError'));
            console.error(error);
            navigate('/promotions');
        }
    };

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = t('validation.promotionNameRequired');
        }
        if (!formData.type) {
            newErrors.type = t('validation.typeRequired');
        }

        if (formData.type === 'BUY_X_GET_Y') {
            if (!formData.buyQuantity || Number(formData.buyQuantity) < 1) {
                newErrors.buyQuantity = t('validation.valuePositive');
            }
            if (!formData.getQuantity || Number(formData.getQuantity) < 1) {
                newErrors.getQuantity = t('validation.valuePositive');
            }
        } else {
            if (!formData.value || isNaN(Number(formData.value)) || Number(formData.value) <= 0) {
                newErrors.value = t('validation.valuePositive');
            }
            if (formData.type === 'PERCENTAGE' && Number(formData.value) > 100) {
                newErrors.value = t('validation.percentageMax');
            }
        }

        if (formData.targetType === 'CATEGORY' && !formData.targetCategoryId) {
            newErrors.targetCategoryId = t('validation.fieldRequired');
        }
        if (formData.targetType === 'VARIANT') {
            if (selectedVariants.length === 0) {
                newErrors.targetVariantId = t('promotions_ext.variantsRequired');
            } else if (selectedVariants.some((v) => isBelowMinimum(v, formData.minimumAmount))) {
                newErrors.targetVariantId = t('promotions_ext.belowMinimum');
            }
        }

        if (isSuperAdminWithoutStore && !isEdit && !formData.storeId) {
            newErrors.storeId = t('validation.storeRequired');
        }
        if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
            newErrors.endDate = t('validation.endDateAfterStart');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            toast.error(t('common.saveError'));
            return;
        }

        setLoading(true);
        try {
            const isBuyXGetY = formData.type === 'BUY_X_GET_Y';
            const payload = {
                ...formData,
                // BUY_X_GET_Y has no percentage/amount — `value` stays
                // required AND must be > 0 on the backend regardless of type
                // (@DecimalMin(inclusive = false)), so 0 gets rejected with
                // a 400 — send 1 as a harmless placeholder instead; it's
                // never actually read for this promotion type.
                value: isBuyXGetY ? 1 : (formData.value ? Number(formData.value) : null),
                buyQuantity: isBuyXGetY && formData.buyQuantity ? Number(formData.buyQuantity) : null,
                getQuantity: isBuyXGetY && formData.getQuantity ? Number(formData.getQuantity) : null,
                minimumAmount: formData.minimumAmount ? Number(formData.minimumAmount) : null,
                usageLimit: formData.usageLimit ? Number(formData.usageLimit) : null,
                customerId: formData.customerId || null,
                startDate: formData.startDate ? `${formData.startDate}T00:00:00` : null,
                endDate: formData.endDate ? `${formData.endDate}T23:59:59` : null,
                // Each targeting mode only sends its own reference(s) — sending
                // more than one kind at once would let a promotion apply via
                // two unrelated paths at once (see PromotionCalculationService).
                productIds: formData.targetType === 'PRODUCT' ? selectedProducts.map((p) => p.id) : [],
                targetCategoryId: formData.targetType === 'CATEGORY' ? formData.targetCategoryId : null,
                targetVariantId: formData.targetType === 'VARIANT' ? (selectedVariants[0]?.id || null) : null,
                additionalTargetVariantIds:
                    formData.targetType === 'VARIANT' ? selectedVariants.slice(1).map((v) => v.id) : [],
                additionalStoreIds: formData.additionalStoreIds,
            };

            if (isEdit) {
                await promotionService.updatePromotion(id, payload);
                toast.success(t('promotions.updateSuccess'));
            } else {
                await promotionService.createPromotion(payload);
                toast.success(t('promotions.createSuccess'));
            }
            navigate('/promotions');
        } catch (error) {
            toast.error(
                error.response?.data?.message || t('common.saveError')
            );
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/promotions')}>
                    {t('common.back')}
                </Button>
                <Typography variant="h4">
                    {isEdit ? t('promotions.editPromotion') : t('promotions.createPromotion')}
                </Typography>
            </Box>

            <form onSubmit={handleSubmit}>
                <Card>
                    <CardContent>
                        <Grid container spacing={3}>
                            {/* Store Selector - SUPER_ADMIN only on CREATE */}
                            {isSuperAdminWithoutStore && !isEdit && (
                                <Grid item xs={12}>
                                    <FormControl fullWidth required error={Boolean(errors.storeId)}>
                                        <InputLabel>{t('roles.store')}</InputLabel>
                                        <Select
                                            value={formData.storeId}
                                            label={t('roles.store')}
                                            onChange={(e) => handleChange('storeId', e.target.value)}
                                        >
                                            {stores.map((store) => (
                                                <MenuItem key={store.id} value={store.id}>
                                                    {store.name} ({store.code})
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors.storeId && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                                {errors.storeId}
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Grid>
                            )}

                            {/* Name */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    required
                                    label={t('common.name')}
                                    value={formData.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    error={Boolean(errors.name)}
                                    helperText={errors.name}
                                />
                            </Grid>

                            {/* Code */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('promotions.code')}
                                    value={formData.code}
                                    onChange={(e) => handleChange('code', e.target.value)}
                                    placeholder={t('promotions.codePlaceholder')}
                                />
                            </Grid>

                            {/* Type */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth required error={Boolean(errors.type)}>
                                    <InputLabel>{t('promotions.type')}</InputLabel>
                                    <Select
                                        value={formData.type}
                                        label={t('promotions.type')}
                                        onChange={(e) => handleChange('type', e.target.value)}
                                    >
                                        <MenuItem value="PERCENTAGE">{t('promotions.types.PERCENTAGE')}</MenuItem>
                                        <MenuItem value="FIXED_AMOUNT">{t('promotions.types.FIXED_AMOUNT')}</MenuItem>
                                        <MenuItem value="BUY_X_GET_Y">{t('promotions.types.BUY_X_GET_Y')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Value — PERCENTAGE/FIXED_AMOUNT only */}
                            {formData.type !== 'BUY_X_GET_Y' ? (
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        label={t('promotions.value')}
                                        type="number"
                                        value={formData.value}
                                        onChange={(e) => handleChange('value', e.target.value)}
                                        error={Boolean(errors.value)}
                                        helperText={errors.value}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {formData.type === 'PERCENTAGE' ? '%' : t('promotions.amountAdornment')}
                                                </InputAdornment>
                                            ),
                                        }}
                                        inputProps={{ min: 0, step: 'any' }}
                                    />
                                </Grid>
                            ) : (
                                <>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            required
                                            label={t('promotions.buyQuantity')}
                                            type="number"
                                            value={formData.buyQuantity}
                                            onChange={(e) => handleChange('buyQuantity', e.target.value)}
                                            error={Boolean(errors.buyQuantity)}
                                            helperText={errors.buyQuantity}
                                            inputProps={{ min: 1, step: 1 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            required
                                            label={t('promotions.getQuantity')}
                                            type="number"
                                            value={formData.getQuantity}
                                            onChange={(e) => handleChange('getQuantity', e.target.value)}
                                            error={Boolean(errors.getQuantity)}
                                            helperText={errors.getQuantity}
                                            inputProps={{ min: 1, step: 1 }}
                                        />
                                    </Grid>
                                </>
                            )}

                            {/* Minimum Amount */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('promotions.minOrderAmount')}
                                    type="number"
                                    value={formData.minimumAmount}
                                    onChange={(e) => handleChange('minimumAmount', e.target.value)}
                                    inputProps={{ min: 0, step: 'any' }}
                                    helperText={formData.targetType === 'VARIANT'
                                        ? t('promotions_ext.minimumPriceHelper')
                                        : t('promotions_ext.minimumAmountHelper')}
                                />
                            </Grid>

                            {/* Usage Limit */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('promotions.maxUsageCount')}
                                    type="number"
                                    value={formData.usageLimit}
                                    onChange={(e) => handleChange('usageLimit', e.target.value)}
                                    placeholder={t('promotions.unlimitedPlaceholder')}
                                    inputProps={{ min: 1, step: 1 }}
                                />
                            </Grid>

                            {/* Start Date */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('promotions.startDate')}
                                    type="date"
                                    value={formData.startDate}
                                    onChange={(e) => handleChange('startDate', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            {/* End Date */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('promotions.endDate')}
                                    type="date"
                                    value={formData.endDate}
                                    onChange={(e) => handleChange('endDate', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    error={Boolean(errors.endDate)}
                                    helperText={errors.endDate}
                                />
                            </Grid>

                            {/* Customer (optional) */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>{t('customers.customer')}</InputLabel>
                                    <Select
                                        value={formData.customerId}
                                        label={t('customers.customer')}
                                        onChange={(e) => handleChange('customerId', e.target.value)}
                                    >
                                        <MenuItem value="">{t('common.all')}</MenuItem>
                                        {customers.map((c) => (
                                            <MenuItem key={c.id} value={c.id}>
                                                {c.firstName} {c.lastName}
                                                {c.email ? ` (${c.email})` : ''}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Target — what the promotion applies to */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>{t('promotions.target')}</InputLabel>
                                    <Select
                                        value={formData.targetType}
                                        label={t('promotions.target')}
                                        onChange={(e) => handleChange('targetType', e.target.value)}
                                    >
                                        <MenuItem value="ALL_PRODUCTS">{t('promotions.targetTypes.ALL_PRODUCTS')}</MenuItem>
                                        <MenuItem value="CATEGORY">{t('promotions.targetTypes.CATEGORY')}</MenuItem>
                                        <MenuItem value="PRODUCT">{t('promotions.targetTypes.PRODUCT')}</MenuItem>
                                        <MenuItem value="VARIANT">{t('promotions.targetTypes.VARIANT')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Category — only when targetType === CATEGORY */}
                            {formData.targetType === 'CATEGORY' && (
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth required error={Boolean(errors.targetCategoryId)}>
                                        <InputLabel>{t('promotions.selectCategory')}</InputLabel>
                                        <Select
                                            value={formData.targetCategoryId}
                                            label={t('promotions.selectCategory')}
                                            onChange={(e) => handleChange('targetCategoryId', e.target.value)}
                                        >
                                            {categories.map((cat) => (
                                                <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                                            ))}
                                        </Select>
                                        {errors.targetCategoryId && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                                {errors.targetCategoryId}
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Grid>
                            )}

                            {/* Products — only when targetType === PRODUCT */}
                            {formData.targetType === 'PRODUCT' && (
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        multiple
                                        options={products}
                                        getOptionLabel={(option) => `${option.name} (${option.sku || t('promotions_ext.noSku')})`}
                                        value={selectedProducts}
                                        onChange={(_, newValue) => setSelectedProducts(newValue)}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    label={option.name}
                                                    size="small"
                                                    {...getTagProps({ index })}
                                                />
                                            ))
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t('nav.products')}
                                                placeholder={t('common.all')}
                                            />
                                        )}
                                    />
                                </Grid>
                            )}

                            {/* Variants — any number, from any products */}
                            {formData.targetType === 'VARIANT' && (
                                <Grid item xs={12}>
                                    <PromotionVariantPicker
                                        products={products}
                                        selectedVariants={selectedVariants}
                                        onChange={(next) => {
                                            setSelectedVariants(next);
                                            if (errors.targetVariantId) {
                                                setErrors((prev) => ({ ...prev, targetVariantId: null }));
                                            }
                                        }}
                                        promotion={formData}
                                        error={errors.targetVariantId}
                                    />
                                </Grid>
                            )}

                            {/* Magasins — extra stores this promotion also applies to,
                                beyond its owning store (checked/disabled below). */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {t('promotions_ext.stores')}
                                </Typography>
                                <FormGroup row>
                                    {stores.map((s) => {
                                        const isOwnStore = isEdit
                                            ? s.id === formData.storeId
                                            : (isSuperAdminWithoutStore ? s.id === formData.storeId : s.id === user?.store?.id);
                                        const checked = isOwnStore || formData.additionalStoreIds.includes(s.id);
                                        return (
                                            <FormControlLabel
                                                key={s.id}
                                                control={
                                                    <Checkbox
                                                        checked={checked}
                                                        disabled={isOwnStore}
                                                        onChange={(e) => {
                                                            const next = e.target.checked
                                                                ? [...formData.additionalStoreIds, s.id]
                                                                : formData.additionalStoreIds.filter((id) => id !== s.id);
                                                            handleChange('additionalStoreIds', next);
                                                        }}
                                                    />
                                                }
                                                label={`${s.name} (${s.code})`}
                                            />
                                        );
                                    })}
                                </FormGroup>
                            </Grid>

                            {/* Active Status */}
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.isActive}
                                            onChange={(e) => handleChange('isActive', e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label={t('common.active')}
                                />
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                                    {t('common.inactive')}
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Action Buttons */}
                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={loading}
                    >
                        {loading ? t('common.save') + '...' : isEdit ? t('promotions.editPromotion') : t('promotions.createPromotion')}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/promotions')}
                        disabled={loading}
                    >
                        {t('common.cancel')}
                    </Button>
                </Box>
            </form>
        </Box>
    );
};

export default PromotionForm;
